import type { Book } from "@book-club/shared";
import type { BookFormData } from "@/features/books/schemas/bookForm.schema";
import type { CreateBookInput } from "@/features/books/types";

/**
 * Converts a Book to BookFormData format for form initialization
 */
export function bookToFormData(book: Book): BookFormData {
    if (book.type === "specificBook") {
        return {
            amazonUrl: book.amazonUrl ?? "",
            author: book.author,
            coverUrl: book.coverUrl ?? "",
            description: book.description ?? "",
            isbn: book.isbn ?? "",
            title: book.title,
            type: "specificBook",
        };
    }
    if (book.type === "authorFocus") {
        return {
            amazonUrl: book.amazonUrl ?? "",
            author: book.author,
            type: "authorFocus",
        };
    }
    return {
        type: "freeChoice",
    };
}

/**
 * Gets default form values based on mode and initial data
 */
export function getDefaultFormValues(mode: "add" | "edit", initialData?: Book): BookFormData {
    if (mode === "edit" && initialData) {
        return bookToFormData(initialData);
    }
    // In add mode, default to specificBook type with empty fields
    return {
        amazonUrl: "",
        author: "",
        coverUrl: "",
        description: "",
        isbn: "",
        title: "",
        type: "specificBook",
    };
}

/**
 * Prepares CreateBookInput for specificBook type
 * Validates that title and author are present and non-empty
 */
export function prepareSpecificBookData(formData: BookFormData): CreateBookInput | null {
    if (formData.type !== "specificBook") {
        return null;
    }
    const trimmedTitle = formData.title?.trim();
    const trimmedAuthor = formData.author?.trim();
    if (!trimmedTitle || !trimmedAuthor) {
        return null;
    }
    return {
        amazonUrl: formData.amazonUrl?.trim() || undefined,
        author: trimmedAuthor,
        coverUrl: formData.coverUrl?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        isbn: formData.isbn?.trim() || undefined,
        title: trimmedTitle,
        type: "specificBook",
    };
}

/**
 * Prepares CreateBookInput for authorFocus type
 * Validates that author is present and non-empty
 * Auto-generates Amazon URL from author if not already provided
 */
export function prepareAuthorFocusData(formData: BookFormData): CreateBookInput | null {
    if (formData.type !== "authorFocus") {
        return null;
    }
    const trimmedAuthor = formData.author?.trim();
    if (!trimmedAuthor) {
        return null;
    }
    // Auto-generate Amazon URL from author if not already provided
    const amazonUrl =
        formData.amazonUrl?.trim() ||
        (trimmedAuthor ? `https://www.amazon.co.uk/s?k=${encodeURIComponent(trimmedAuthor)}` : undefined);
    return {
        amazonUrl,
        author: trimmedAuthor,
        type: "authorFocus",
    };
}

/**
 * Prepares CreateBookInput for freeChoice type
 * No validation needed - only type is required
 */
export function prepareFreeChoiceData(): CreateBookInput {
    return {
        type: "freeChoice",
    };
}
